import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable, signal } from '@angular/core';

type Theme = 'light' | 'dark';

export interface ColorScheme {
  id: string;
  label: string;
  /** Main accent color (light theme) */
  accent: string;
  /** Hover accent (light theme) */
  accentHover: string;
  /** Very light background tint */
  accentLight: string;
  /** Light surface tint */
  accentSurface: string;
  /** Dark theme accent */
  accentDark: string;
  /** Dark theme accent hover */
  accentDarkHover: string;
  /** Sidebar gradient start */
  sidebarFrom: string;
  /** Sidebar gradient end */
  sidebarTo: string;
  /** Preview swatch color */
  swatch: string;
}

export const COLOR_SCHEMES: ColorScheme[] = [
  {
    id: 'indigo',
    label: 'Индиго',
    accent: '#4f46e5',
    accentHover: '#4338ca',
    accentLight: '#eef2ff',
    accentSurface: '#e0e7ff',
    accentDark: '#6366f1',
    accentDarkHover: '#818cf8',
    sidebarFrom: '#0f172a',
    sidebarTo: '#1a2332',
    swatch: '#6366f1',
  },
  {
    id: 'blue',
    label: 'Синий',
    accent: '#2563eb',
    accentHover: '#1d4ed8',
    accentLight: '#eff6ff',
    accentSurface: '#dbeafe',
    accentDark: '#3b82f6',
    accentDarkHover: '#60a5fa',
    sidebarFrom: '#0c1929',
    sidebarTo: '#152238',
    swatch: '#3b82f6',
  },
  {
    id: 'violet',
    label: 'Фиолетовый',
    accent: '#7c3aed',
    accentHover: '#6d28d9',
    accentLight: '#f5f3ff',
    accentSurface: '#ede9fe',
    accentDark: '#8b5cf6',
    accentDarkHover: '#a78bfa',
    sidebarFrom: '#13062e',
    sidebarTo: '#1e1038',
    swatch: '#8b5cf6',
  },
  {
    id: 'rose',
    label: 'Розовый',
    accent: '#e11d48',
    accentHover: '#be123c',
    accentLight: '#fff1f2',
    accentSurface: '#ffe4e6',
    accentDark: '#f43f5e',
    accentDarkHover: '#fb7185',
    sidebarFrom: '#1a0a10',
    sidebarTo: '#2a1520',
    swatch: '#f43f5e',
  },
  {
    id: 'emerald',
    label: 'Изумрудный',
    accent: '#059669',
    accentHover: '#047857',
    accentLight: '#ecfdf5',
    accentSurface: '#d1fae5',
    accentDark: '#10b981',
    accentDarkHover: '#34d399',
    sidebarFrom: '#06150f',
    sidebarTo: '#0d2418',
    swatch: '#10b981',
  },
  {
    id: 'amber',
    label: 'Янтарный',
    accent: '#d97706',
    accentHover: '#b45309',
    accentLight: '#fffbeb',
    accentSurface: '#fef3c7',
    accentDark: '#f59e0b',
    accentDarkHover: '#fbbf24',
    sidebarFrom: '#1a1308',
    sidebarTo: '#2a2010',
    swatch: '#f59e0b',
  },
  {
    id: 'teal',
    label: 'Бирюзовый',
    accent: '#0d9488',
    accentHover: '#0f766e',
    accentLight: '#f0fdfa',
    accentSurface: '#ccfbf1',
    accentDark: '#14b8a6',
    accentDarkHover: '#2dd4bf',
    sidebarFrom: '#061615',
    sidebarTo: '#0d2422',
    swatch: '#14b8a6',
  },
  {
    id: 'cyan',
    label: 'Голубой',
    accent: '#0891b2',
    accentHover: '#0e7490',
    accentLight: '#ecfeff',
    accentSurface: '#cffafe',
    accentDark: '#06b6d4',
    accentDarkHover: '#22d3ee',
    sidebarFrom: '#06171c',
    sidebarTo: '#0d252e',
    swatch: '#06b6d4',
  },
];

const THEME_STORAGE_KEY = 'app-theme';
const COLOR_SCHEME_STORAGE_KEY = 'app-color-scheme';
const DARK_THEME_CLASS = 'theme-dark';
const NO_TRANSITION_CLASS = 'theme-no-transition';
const VIEW_TRANSITION_STYLE_ID = 'theme-view-transition-styles';
const FALLBACK_DURATION = 240;
const FALLBACK_MIDPOINT = 120;

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly themeSignal = signal<Theme>(this.getInitialTheme());
  private readonly colorSchemeSignal = signal<ColorScheme>(this.getInitialColorScheme());
  private isTransitioning = false;

  public readonly theme = this.themeSignal.asReadonly();
  public readonly isDark = computed(() => this.themeSignal() === 'dark');
  public readonly colorScheme = this.colorSchemeSignal.asReadonly();
  public readonly colorSchemes = COLOR_SCHEMES;

  constructor() {
    this.addViewTransitionStyles();
    this.applyTheme(this.themeSignal());
    this.applyColorScheme(this.colorSchemeSignal());
  }

  public toggleTheme(event?: MouseEvent): void {
    const nextTheme: Theme = this.themeSignal() === 'dark' ? 'light' : 'dark';
    this.themeSignal.set(nextTheme);
    localStorage.setItem(THEME_STORAGE_KEY, nextTheme);

    if (event && 'startViewTransition' in this.document) {
      this.circularReveal(nextTheme, event.clientX, event.clientY);
    } else {
      this.crossfade(nextTheme);
    }
  }

  public setColorScheme(scheme: ColorScheme): void {
    this.colorSchemeSignal.set(scheme);
    localStorage.setItem(COLOR_SCHEME_STORAGE_KEY, scheme.id);
    this.applyColorScheme(scheme);
  }

  /** Circular reveal через View Transitions API */
  private circularReveal(theme: Theme, x: number, y: number): void {
    if (this.isTransitioning) {
      this.applyTheme(theme);
      return;
    }

    this.isTransitioning = true;

    const { body, documentElement } = this.document;

    // Радиус круга = расстояние до самого дальнего угла viewport
    const maxRadius = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));

    documentElement.style.setProperty('--theme-x', `${x}px`);
    documentElement.style.setProperty('--theme-y', `${y}px`);
    documentElement.style.setProperty('--theme-r', `${maxRadius}px`);

    // Prevent component-level CSS transitions from being captured halfway
    // through their animation in the new theme snapshot.
    body.classList.add(NO_TRANSITION_CLASS);

    const transition = this.document.startViewTransition(() => {
      this.applyTheme(theme);
    });

    void transition.ready
      .then(() => {
        documentElement.animate(
          { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${maxRadius}px at ${x}px ${y}px)`] },
          {
            duration: 500,
            easing: 'ease-in',
            pseudoElement: '::view-transition-new(root)',
          }
        );
      })
      .catch((): void => undefined)
      .finally(() => body.classList.remove(NO_TRANSITION_CLASS));

    void transition.finished
      .catch((): void => undefined)
      .finally(() => {
        body.classList.remove(NO_TRANSITION_CLASS);
        this.isTransitioning = false;
      });
  }

  /** Fallback crossfade для браузеров без View Transitions API */
  private crossfade(theme: Theme): void {
    const { body } = this.document;

    if (this.isTransitioning) {
      this.applyTheme(theme);
      return;
    }

    this.isTransitioning = true;
    body.classList.add('theme-fade');

    setTimeout(() => {
      body.classList.add(NO_TRANSITION_CLASS);
      this.applyTheme(theme);

      requestAnimationFrame(() => {
        body.classList.remove(NO_TRANSITION_CLASS);
      });
    }, FALLBACK_MIDPOINT);

    setTimeout(() => {
      body.classList.remove('theme-fade');
      this.isTransitioning = false;
    }, FALLBACK_DURATION);
  }

  private applyTheme(theme: Theme): void {
    this.document.body.classList.toggle(DARK_THEME_CLASS, theme === 'dark');
    this.document.documentElement.style.colorScheme = theme;
    this.applyColorScheme(this.colorSchemeSignal());
  }

  private addViewTransitionStyles(): void {
    if (!('startViewTransition' in this.document) || this.document.getElementById(VIEW_TRANSITION_STYLE_ID)) {
      return;
    }

    const style = this.document.createElement('style');
    style.id = VIEW_TRANSITION_STYLE_ID;
    style.textContent = '::view-transition-image-pair(root) { isolation: auto; }';
    this.document.head.append(style);
  }

  private applyColorScheme(scheme: ColorScheme): void {
    const root = this.document.documentElement;
    const isDark = this.themeSignal() === 'dark';
    const accent = isDark ? scheme.accentDark : scheme.accent;

    root.style.setProperty('--app-accent', accent);
    root.style.setProperty('--app-accent-hover', isDark ? scheme.accentDarkHover : scheme.accentHover);
    root.style.setProperty('--app-accent-light', isDark ? `color-mix(in srgb, ${accent} 12%, transparent)` : scheme.accentLight);
    root.style.setProperty('--app-accent-surface', isDark ? `color-mix(in srgb, ${accent} 20%, transparent)` : scheme.accentSurface);
    root.style.setProperty('--sidebar-from', scheme.sidebarFrom);
    root.style.setProperty('--sidebar-to', scheme.sidebarTo);
  }

  private getInitialTheme(): Theme {
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);

    if (storedTheme === 'dark' || storedTheme === 'light') {
      return storedTheme;
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private getInitialColorScheme(): ColorScheme {
    const storedId = localStorage.getItem(COLOR_SCHEME_STORAGE_KEY);
    return COLOR_SCHEMES.find(s => s.id === storedId) ?? COLOR_SCHEMES[0];
  }
}
